import { useEffect } from 'react';
import { tg, ready } from './telegram';

/** Макет телефона: та же ширина, что у мобильной раскладки виджета. */
const DESIGN_WIDTH = 390;

/**
 * Оболочка телеграма: размеры окна, тема и безопасные зоны.
 *
 * Высоту окна телеграм сообщает сам и меняет её на ходу — когда человек
 * разворачивает приложение или когда снизу выезжает клавиатура. Считать
 * её по `100vh` нельзя: на телефоне это высота экрана, а не окна,
 * и нижняя кнопка уезжает под клавиатуру.
 *
 * `viewportStableHeight` — высота без клавиатуры. Именно она годится
 * для раскладки: иначе экран прыгает на каждое касание поля ввода.
 */
export function useTelegramShell(root: HTMLElement | null): void {
  useEffect(() => {
    if (!root) return;

    const app = tg();
    ready();

    const apply = (): void => {
      // Раскладка здесь всегда узкая: телеграм открывает приложение
      // в телефоне, а на настольном клиенте — в окне телефонной ширины.
      root.dataset.layout = 'mobile';

      // Ширина ограничена макетом. На настольном клиенте окно бывает шире
      // телефонного, и без предела вёрстка растягивалась бы в полосу.
      const width = Math.min(window.innerWidth, DESIGN_WIDTH);
      const window_height = app?.viewportStableHeight ?? window.innerHeight;

      // Единица считается только от ширины.
      //
      // Соблазн делить ещё и на высоту велик: на настольном клиенте окно
      // ниже телефонного, и при делении всё бы «влезло». Но влезло бы
      // ценой кегля: шестнадцать точек превратились бы в тринадцать,
      // а это ниже границы читаемости. Лучше прокрутка, чем мелкий текст.
      const unit = Math.min(width / DESIGN_WIDTH, 1.45);

      // Оболочка ровно по окну телеграма, ни точкой больше.
      //
      // Здесь стояло «не меньше семисот точек» — чтобы на настольном
      // клиенте, где окно ниже, вёрстка не ужималась. Это было ошибкой:
      // оболочка становилась выше окна, и прокручивалась страница целиком,
      // вместе с шапкой, а экраны всё равно обрезались — у оболочки стоит
      // overflow: hidden. Прокрутку теперь даёт средняя полоса, а шапка
      // и полоса шагов остаются на месте.
      root.style.setProperty('--ls-u', `${unit.toFixed(4)}px`);
      root.style.setProperty('--ls-shell-w', `${width}px`);
      root.style.setProperty('--ls-shell-h', `${window_height}px`);
      root.style.setProperty('--ls-window-h', `${window_height}px`);
    };

    apply();

    app?.onEvent('viewportChanged', apply);
    app?.onEvent('themeChanged', apply);
    window.addEventListener('resize', apply);

    return () => {
      app?.offEvent('viewportChanged', apply);
      app?.offEvent('themeChanged', apply);
      window.removeEventListener('resize', apply);
    };
  }, [root]);
}
